/**
 * Stroke glyphs for the pre-send review popup. The paths are the Figma "Pre-send review" icons
 * (file `InkFree web application`, node 9012-2849) in their own viewBox, so a glyph keeps the design's
 * shape and stroke weight at any size. The design bakes its own colours into the files; these paint with
 * `currentColor` instead, so each one takes the app's existing palette from where it sits. Decorative
 * (`aria-hidden`): every glyph sits next to text that says the same thing.
 */
import React from "react";

interface ReviewIconProps {
  size?: number;
}

const Glyph = ({
  size,
  box,
  strokeWidth,
  children,
}: {
  size: number;
  box: number;
  strokeWidth: number;
  children: React.ReactNode;
}) => (
  <svg
    width={size}
    height={size}
    viewBox={`0 0 ${box} ${box}`}
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
    style={{ display: "block", flexShrink: 0 }}
  >
    {children}
  </svg>
);

/**
 * Chevron -- a document that opens and closes. Not in the Figma (its review shows one document): Lucide's
 * chevron-down, the set the design's other glyphs come from.
 */
export const ReviewChevronIcon = ({ size = 16 }: ReviewIconProps) => (
  <Glyph size={size} box={24} strokeWidth={2}>
    <path d="m6 9 6 6 6-6" />
  </Glyph>
);

/** Icon/circlecheck -- ready. */
export const ReviewReadyIcon = ({ size = 20 }: ReviewIconProps) => (
  <Glyph size={size} box={20} strokeWidth={1.66667}>
    <path d="M18.3333 10C18.3333 14.6024 14.6024 18.3333 10 18.3333C5.39763 18.3333 1.66667 14.6024 1.66667 10C1.66667 5.39763 5.39763 1.66667 10 1.66667C14.6024 1.66667 18.3333 5.39763 18.3333 10Z" />
    <path d="M7.5 10L9.16667 11.6667L12.5 8.33333" />
  </Glyph>
);

/** Icon/check -- the Ready status pill. */
export const ReviewCheckIcon = ({ size = 13 }: ReviewIconProps) => (
  <Glyph size={size} box={13} strokeWidth={1.08333}>
    <path d="M10.8333 3.25L4.875 9.20833L2.16667 6.5" />
  </Glyph>
);

/** Icon/alert -- needs attention. */
export const ReviewWarningIcon = ({ size = 20 }: ReviewIconProps) => (
  <Glyph size={size} box={20} strokeWidth={1.66667}>
    <path d="M18.1083 15L11.4417 3.33333C11.2963 3.07684 11.0855 2.86349 10.8308 2.71506C10.576 2.56663 10.2865 2.48842 9.99167 2.48842C9.69684 2.48842 9.4073 2.56663 9.15256 2.71506C8.89783 2.86349 8.68703 3.07684 8.54167 3.33333L1.875 15C1.72807 15.2545 1.65102 15.5433 1.65167 15.8371C1.65233 16.1309 1.73065 16.4194 1.87871 16.6732C2.02676 16.927 2.23929 17.1372 2.49475 17.2824C2.7502 17.4276 3.03951 17.5026 3.33333 17.5H16.6667C16.9591 17.4997 17.2463 17.4225 17.4994 17.2761C17.7525 17.1297 17.9627 16.9192 18.1088 16.6659C18.2548 16.4126 18.3317 16.1253 18.3316 15.8329C18.3315 15.5405 18.2545 15.2532 18.1083 15Z" />
    <path d="M10 7.5V10.8333" />
    <path d="M10 14.1667H10.0083" />
  </Glyph>
);

/** Icon/eyeoff -- not reviewed, couldn't check. */
export const ReviewNotCheckedIcon = ({ size = 20 }: ReviewIconProps) => (
  <Glyph size={size} box={20} strokeWidth={1.66667}>
    <path d="M8.94417 4.23C10.8853 3.99867 12.8488 4.40901 14.5349 5.39837C16.2209 6.38773 17.5368 7.90173 18.2817 9.70917C18.3511 9.89626 18.3511 10.1021 18.2817 10.2892C17.9754 11.0317 17.5707 11.7296 17.0783 12.3642" />
    <path d="M11.7367 11.7983C11.2652 12.2537 10.6337 12.5057 9.97816 12.5C9.32267 12.4943 8.69564 12.2314 8.23212 11.7679C7.7686 11.3044 7.50568 10.6773 7.49998 10.0218C7.49429 9.36634 7.74627 8.73484 8.20167 8.26333" />
    <path d="M14.5658 14.5825C13.4604 15.2373 12.2271 15.6467 10.9495 15.7828C9.67188 15.919 8.37996 15.7787 7.16136 15.3716C5.94276 14.9644 4.82599 14.2999 3.88684 13.4231C2.94769 12.5463 2.20813 11.4778 1.71833 10.29C1.64888 10.1029 1.64888 9.8971 1.71833 9.71C2.45719 7.91821 3.75723 6.41437 5.42333 5.42417" />
    <path d="M1.66667 1.66667L18.3333 18.3333" />
  </Glyph>
);

/** Icon/cloudoff -- the review could not run (5xx or timeout). */
export const ReviewUnavailableIcon = ({ size = 20 }: ReviewIconProps) => (
  <Glyph size={size} box={20} strokeWidth={1.66667}>
    <path d="M1.66667 1.66667L18.3333 18.3333" />
    <path d="M4.81833 4.81833C3.65689 5.41886 2.73088 6.39218 2.18891 7.58208C1.64695 8.77198 1.52046 10.1095 1.82975 11.3799C2.13903 12.6503 2.86616 13.7799 3.89443 14.5875C4.9227 15.3952 6.19249 15.8339 7.5 15.8333H14.5833C14.9523 15.8336 15.3193 15.7794 15.6725 15.6725" />
    <path d="M17.9433 13.75C18.227 13.1782 18.3601 12.5435 18.3299 11.9059C18.2997 11.2683 18.1072 10.649 17.7708 10.1066C17.4343 9.56419 16.9649 9.11664 16.4072 8.80633C15.8494 8.49601 15.2216 8.33321 14.5833 8.33333H13.0917C12.7729 7.26606 12.1556 6.31221 11.3125 5.58428C10.4694 4.85636 9.43568 4.38472 8.33333 4.225" />
  </Glyph>
);

/** Icon/clock -- 429. */
export const ReviewClockIcon = ({ size = 20 }: ReviewIconProps) => (
  <Glyph size={size} box={20} strokeWidth={1.66667}>
    <path d="M18.3333 10C18.3333 14.6024 14.6024 18.3333 10 18.3333C5.39763 18.3333 1.66667 14.6024 1.66667 10C1.66667 5.39763 5.39763 1.66667 10 1.66667C14.6024 1.66667 18.3333 5.39763 18.3333 10Z" />
    <path d="M10 5V10L13.3333 11.6667" />
  </Glyph>
);

/** Icon/lock -- session expired. */
export const ReviewLockIcon = ({ size = 20 }: ReviewIconProps) => (
  <Glyph size={size} box={20} strokeWidth={1.66667}>
    <path d="M4.16667 9.16667H15.8333C16.7538 9.16667 17.5 9.91286 17.5 10.8333V16.6667C17.5 17.5871 16.7538 18.3333 15.8333 18.3333H4.16667C3.24619 18.3333 2.5 17.5871 2.5 16.6667V10.8333C2.5 9.91286 3.24619 9.16667 4.16667 9.16667Z" />
    <path d="M5.83333 9.16667V5.83333C5.83333 4.72826 6.27232 3.66846 7.05372 2.88706C7.83512 2.10565 8.89493 1.66667 10 1.66667C11.1051 1.66667 12.1649 2.10565 12.9463 2.88706C13.7277 3.66846 14.1667 4.72826 14.1667 5.83333V9.16667" />
  </Glyph>
);

/**
 * Icon/spin -- checking. A faint full ring with a quarter arc over it; the caller rotates it. The ring is
 * the same colour at low opacity, which is how the design's light track reads against its tint.
 */
export const ReviewSpinnerIcon = ({ size = 20 }: ReviewIconProps) => (
  <Glyph size={size} box={20} strokeWidth={1.66667}>
    <path
      strokeOpacity={0.3}
      d="M17.5 10C17.5 14.1421 14.1421 17.5 10 17.5C5.85786 17.5 2.5 14.1421 2.5 10C2.5 5.85786 5.85786 2.5 10 2.5C14.1421 2.5 17.5 5.85786 17.5 10Z"
    />
    <path d="M17.5 10C17.5 8.01088 16.7098 6.10322 15.3033 4.6967C13.8968 3.29018 11.9891 2.5 10 2.5" />
  </Glyph>
);

/** Icon/file -- a document row. */
export const ReviewDocumentIcon = ({ size = 18 }: ReviewIconProps) => (
  <Glyph size={size} box={18} strokeWidth={1.5}>
    <path d="M11.25 1.5H4.5C4.10218 1.5 3.72064 1.65804 3.43934 1.93934C3.15804 2.22064 3 2.60218 3 3V15C3 15.3978 3.15804 15.7794 3.43934 16.0607C3.72064 16.342 4.10218 16.5 4.5 16.5H13.5C13.8978 16.5 14.2794 16.342 14.5607 16.0607C14.842 15.7794 15 15.3978 15 15V5.25L11.25 1.5Z" />
    <path d="M10.5 1.5V4.5C10.5 4.89782 10.658 5.27936 10.9393 5.56066C11.2206 5.84196 11.6022 6 12 6H15" />
    <path d="M7.5 6.75H6" />
    <path d="M12 9.75H6" />
    <path d="M12 12.75H6" />
  </Glyph>
);

/** Icon/info -- an `info` notice. */
export const ReviewInfoIcon = ({ size = 14 }: ReviewIconProps) => (
  <Glyph size={size} box={14} strokeWidth={1.16667}>
    <path d="M12.8333 7C12.8333 10.2217 10.2217 12.8333 7 12.8333C3.77834 12.8333 1.16667 10.2217 1.16667 7C1.16667 3.77834 3.77834 1.16667 7 1.16667C10.2217 1.16667 12.8333 3.77834 12.8333 7Z" />
    <path d="M7 9.33333V7" />
    <path d="M7 4.66667H7.00583" />
  </Glyph>
);

/** Icon/locate -- "Show on page N". */
export const ReviewJumpIcon = ({ size = 13 }: ReviewIconProps) => (
  <Glyph size={size} box={13} strokeWidth={1.08333}>
    <path d="M3.79167 3.79167H9.20833V9.20833" />
    <path d="M3.79167 9.20833L9.20833 3.79167" />
  </Glyph>
);

/** Icon/x -- "Dismiss". */
export const ReviewDismissIcon = ({ size = 12 }: ReviewIconProps) => (
  <Glyph size={size} box={12} strokeWidth={1}>
    <path d="M9 3L3 9" />
    <path d="M3 3L9 9" />
  </Glyph>
);
